import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  catchError,
  exhaustMap,
  filter,
  firstValueFrom,
  from,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { AuthService } from '../data/services/auth.service';
import {
  authCallbackTriggered,
  loginButtonClicked,
  loginError,
  loginNotAuthenticated,
  loginSuccess,
  logoutButtonClicked,
  logoutError,
  logoutSuccess,
  onInitLoginPage,
} from './auth.actions';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { finalize } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from '../../../../environments/environment';
import { ToastrService } from 'ngx-toastr';
import { EventTypes, PublicEventsService } from 'angular-auth-oidc-client';

export class AuthEffects {
  private actions$ = inject(Actions);
  private authService = inject(AuthService);
  loginButtonClicked$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginButtonClicked),
        tap(() => this.authService.login()),
      ),
    { dispatch: false },
  );
  onInitLoginPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(onInitLoginPage),
      switchMap(() =>
        this.authService.checkAuth().pipe(
          map((auth) => {
            if (!auth.isAuthenticated) {
              return loginNotAuthenticated();
            }
            return { type: '[Auth Effects] Check Auth Complete' };
          }),
          catchError((error: { message: string }) =>
            of(loginError({ error: error.message })),
          ),
        ),
      ),
    ),
  );
  logoutEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(logoutButtonClicked),
      exhaustMap(() =>
        this.authService.logout().pipe(
          map(() => logoutSuccess()),
          catchError((error: { message: string }) =>
            of(logoutError({ error: error.message })),
          ),
        ),
      ),
    ),
  );
  private toastr = inject(ToastrService);
  private spinner = inject(NgxSpinnerService);
  authCallbackEffect$ = createEffect(() =>
    this.actions$.pipe(
      ofType(authCallbackTriggered),
      tap(() => this.spinner.show('main')),
      switchMap(() =>
        from(this.authService.handleLoginCallback()).pipe(
          map((res) => {
            if (!res.user?.isActive) {
              throw new Error('USER_DISABLED');
            }
            return loginSuccess({ user: res.user, token: res.token });
          }),
          catchError((error) => {
            console.error('Authentication error:', error);
            const message =
              error?.error?.message ??
              (error instanceof Error
                ? error.message
                : 'Unknown error occurred');
            return of(loginError({ error: message }));
          }),
          finalize(() => this.spinner.hide()),
        ),
      ),
    ),
  );
  private http = inject(HttpClient);
  private router = inject(Router);
  loginCallbackFailed$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginError),
        tap(async ({ error }) => {
          if (
            error &&
            error !== 'USER_DISABLED' &&
            error !== 'USER_NO_ACCESS' &&
            error !== 'USER_PENDING_APPROVAL'
          ) {
            localStorage.setItem('logoutError', error);
            this.toastr.error(error, 'Erro ao fazer login');
            await firstValueFrom(this.authService.logout());
          }

          if (error === 'USER_DISABLED') {
            await this.router.navigate(['/no-access'], {
              queryParams: { reason: 'disabled' },
            });
          } else if (error === 'USER_NO_ACCESS') {
            await this.router.navigate(['/no-access'], {
              queryParams: { reason: 'no-access' },
            });
          } else if (error === 'USER_PENDING_APPROVAL') {
            await this.router.navigate(['/no-access'], {
              queryParams: { reason: 'pending-approval' },
            });
          }
        }),
      ),
    { dispatch: false },
  );

  logoutSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(logoutSuccess),
        tap(() => {
          sessionStorage.clear();
          this.router.navigate(['/login']).then();
        }),
      ),
    { dispatch: false },
  );
  private publicEvents = inject(PublicEventsService);
  silentRenewFailed$ = createEffect(() =>
    this.publicEvents.registerForEvents().pipe(
      filter((event) => event.type === EventTypes.SilentRenewFailed),
      filter(() =>
        Object.keys(sessionStorage).some((k) =>
          k.includes('audaces-web-converter'),
        ),
      ),
      tap(() => {
        localStorage.setItem('postLogoutToast', 'sessionExpired');
      }),
      map(() => logoutButtonClicked()),
    ),
  );
  loginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loginSuccess),
        tap(() => {
          const redirectTo = environment.isSuperAdmin ? '/admin' : '/';
          if (this.router.url !== redirectTo) {
            this.router.navigate([redirectTo]).then();
          }
        }),
      ),
    { dispatch: false },
  );
  private store = inject(Store);
}
